// Peer-to-peer teleconsult call between kiosk and clinician. Signalling goes over the app WebSocket.
// Weak-network fallback (proposal Section 07): video -> audio -> chat, chosen by either side.

import type { Realtime, ServerMessage, SignalData } from './realtime.ts';

export type CallMode = 'video' | 'audio' | 'chat';
export type CallState = 'idle' | 'connecting' | 'connected' | 'failed' | 'ended';

const ICE: RTCConfiguration = {
  // Production deployments add a TURN relay in-country (configured per the data schedule).
  iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
};

export class ConsultCall {
  private pc: RTCPeerConnection | null = null;
  private local: MediaStream | null = null;
  private off: () => void;
  private rt: Realtime;
  private consultId: string;
  private polite: boolean;
  private makingOffer = false;
  onRemote: (s: MediaStream | null) => void = () => {};
  onLocal: (s: MediaStream | null) => void = () => {};
  onState: (s: CallState) => void = () => {};
  onModeRequest: (m: CallMode) => void = () => {};

  /** polite = the kiosk. Only the clinician side (impolite) creates offers; the kiosk answers. */
  constructor(rt: Realtime, consultId: string, polite: boolean) {
    this.rt = rt;
    this.consultId = consultId;
    this.polite = polite;
    this.off = rt.on((m) => this.onMessage(m));
  }

  async start(mode: CallMode) {
    this.stopMedia();
    this.pc?.close();
    if (mode === 'chat') {
      this.onState('connected');
      return;
    }
    this.onState('connecting');
    try {
      this.local = await navigator.mediaDevices.getUserMedia({ audio: true, video: mode === 'video' ? { width: 640, height: 480, frameRate: 15 } : false });
    } catch {
      // No camera/microphone permission or hardware — fall back to chat.
      this.onState('failed');
      return;
    }
    this.onLocal(this.local);
    const pc = new RTCPeerConnection(ICE);
    this.pc = pc;
    for (const t of this.local.getTracks()) pc.addTrack(t, this.local);
    pc.ontrack = (e) => this.onRemote(e.streams[0] ?? null);
    pc.onicecandidate = (e) => e.candidate && this.signal({ kind: 'ice', candidate: e.candidate.toJSON() });
    pc.onconnectionstatechange = () => {
      if (pc.connectionState === 'connected') this.onState('connected');
      if (pc.connectionState === 'failed') this.onState('failed');
    };
    if (!this.polite) pc.onnegotiationneeded = () => this.offer();
    // The kiosk tells the clinician it is ready to receive an offer (covers offers sent before it had media).
    else this.signal({ kind: 'ready' });
  }

  private async offer() {
    const pc = this.pc;
    if (!pc || this.polite) return;
    try {
      this.makingOffer = true;
      await pc.setLocalDescription();
      this.signal({ kind: 'offer', sdp: pc.localDescription!.toJSON() });
    } finally {
      this.makingOffer = false;
    }
  }

  requestMode(mode: CallMode) {
    this.signal({ kind: 'mode', mode });
  }

  private signal(data: SignalData) {
    this.rt.send({ type: 'signal', consultId: this.consultId, data });
  }

  private async onMessage(m: ServerMessage) {
    // The other side arrived after our offer went out: offer again so it is not lost.
    if (m.type === 'peer-joined' && m.consultId === this.consultId && this.pc && this.pc.connectionState !== 'connected') return this.offer();
    if (m.type !== 'signal' || m.consultId !== this.consultId) return;
    const d = m.data;
    if (d.kind === 'mode') return this.onModeRequest(d.mode);
    if (d.kind === 'ready') return this.offer();
    const pc = this.pc;
    if (!pc) return;
    try {
      if (d.kind === 'ice') {
        await pc.addIceCandidate(d.candidate).catch(() => {});
        return;
      }
      const collision = d.kind === 'offer' && (this.makingOffer || pc.signalingState !== 'stable');
      if (collision && !this.polite) return;
      await pc.setRemoteDescription(d.sdp);
      if (d.kind === 'offer') {
        await pc.setLocalDescription();
        this.signal({ kind: 'answer', sdp: pc.localDescription!.toJSON() });
      }
    } catch (e) {
      console.warn('[rtc] signalling error', e);
    }
  }

  private stopMedia() {
    this.local?.getTracks().forEach((t) => t.stop());
    this.local = null;
    this.onLocal(null);
    this.onRemote(null);
  }

  end() {
    this.stopMedia();
    this.pc?.close();
    this.pc = null;
    this.off();
    this.onState('ended');
  }
}
